import { useCallback, useEffect, useRef } from "react";

/**
 * Runs the function at most once every `delay` milliseconds.
 * If the function is called again before the delay has passed, it will not execute until the next time the delay has passed.
 * If the function is called again after the delay has passed, it will execute immediately.
 */
export function useThrottledFunction<Args extends any[], Return>(
  fn: (...args: Args) => Return,
  delay: number,
): (...args: Args) => void {
  const lastCallTimeRef = useRef<number>(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingArgsRef = useRef<Args | null>(null);

  const throttledFn = useCallback(
    (...args: Args) => {
      const now = Date.now();
      const timeSinceLastCall = now - lastCallTimeRef.current;

      const executeFunction = (argsToUse: Args) => {
        fn(...argsToUse);
        lastCallTimeRef.current = Date.now();
      };

      if (timeSinceLastCall >= delay) {
        // Execute immediately if enough time has passed
        executeFunction(args);

        // Clear any pending execution
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
          timeoutRef.current = null;
          pendingArgsRef.current = null;
        }
      } else {
        // Store the args for the next execution
        pendingArgsRef.current = args;

        // Schedule the next execution if not already scheduled
        if (!timeoutRef.current) {
          const remainingDelay = delay - timeSinceLastCall;
          timeoutRef.current = setTimeout(() => {
            if (pendingArgsRef.current) {
              executeFunction(pendingArgsRef.current);
            }
            timeoutRef.current = null;
            pendingArgsRef.current = null;
          }, remainingDelay);
        }
      }
    },
    [fn, delay],
  );

  return throttledFn;
}

type Pending<R> = {
  resolve: (value: R) => void;
  reject: (error: any) => void;
}[];

const DEFAULT_KEY_FN = () => "__default__";

export interface AsyncDebounceOptions<Args extends unknown[]> {
  /** Debounce calls separately per key, so parallel calls don't cancel each other. */
  readonly keyFn?: (...args: Args) => string;
  /**
   * Caps how long continuous rescheduling can defer a call, so sustained
   * activity (e.g. typing without pause) can't starve it indefinitely.
   */
  readonly maxWait?: number;
  /** Run a still-pending call on unmount instead of discarding it. */
  readonly flushOnUnmount?: boolean;
}

/**
 * Debounces an async function while preserving its return type.
 * Only the most recent call within the debounce window will be executed and resolved.
 * Earlier calls will never resolve.
 *
 * @param fn The async function to debounce
 * @param delay The delay in milliseconds
 * @param options Optional behavior tweaks; see {@link AsyncDebounceOptions}.
 * @returns A debounced version of the input function with the same signature
 */
export function useAsyncDebounce<Args extends unknown[], R>(
  fn: (...args: Args) => Promise<R>,
  delay: number,
  options: AsyncDebounceOptions<Args> = {},
): (...args: Args) => Promise<R> {
  const { keyFn = DEFAULT_KEY_FN, maxWait, flushOnUnmount = false } = options;
  const mapRef = useRef<
    Map<
      string,
      {
        args: Args;
        timer: ReturnType<typeof setTimeout> | null;
        pending: Pending<R>;
        firstScheduledAt: number;
      }
    >
  >(new Map());

  // The cleanup runs once with the mount-time closure, so read `fn` and
  // `flushOnUnmount` through refs to flush with the current values.
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const flushOnUnmountRef = useRef(flushOnUnmount);
  flushOnUnmountRef.current = flushOnUnmount;

  useEffect(() => {
    const map = mapRef.current;
    return () => {
      for (const { timer, pending, args } of map.values()) {
        if (timer) {
          clearTimeout(timer);
        }
        if (flushOnUnmountRef.current) {
          const flushed = fnRef.current(...args);
          for (const { resolve, reject } of pending) {
            flushed.then(resolve, reject);
          }
          // Nothing survives to observe the promise; swallow so a failed flush
          // can't surface as an unhandled rejection.
          void flushed.catch(() => {});
          continue;
        }
        for (const { reject } of pending) {
          reject(new Error("Debounced function was unmounted"));
        }
      }
      map.clear();
    };
  }, []);

  return useCallback(
    (...args: Args): Promise<R> => {
      const key = keyFn(...args);
      const now = Date.now();

      let entry = mapRef.current.get(key);
      if (entry && entry.timer) {
        clearTimeout(entry.timer);
      } else {
        entry = { args, timer: null, pending: [], firstScheduledAt: now };
        mapRef.current.set(key, entry);
      }

      entry.args = args;

      const promise = new Promise<R>((resolve, reject) => {
        entry.pending.push({ resolve, reject });
      });

      // Cap the wait so the call fires within maxWait of the window's first call.
      const wait =
        maxWait === undefined
          ? delay
          : Math.max(
              0,
              Math.min(delay, maxWait - (now - entry.firstScheduledAt)),
            );

      entry.timer = setTimeout(async () => {
        mapRef.current.delete(key);

        try {
          const result = await fn(...entry!.args);
          for (const { resolve } of entry.pending) {
            resolve(result);
          }
        } catch (err) {
          for (const { reject } of entry.pending) {
            reject(err);
          }
        }
      }, wait);

      return promise;
    },
    [fn, delay, keyFn, maxWait],
  );
}

/**
 * Wraps an async function so it executes at most once per `minInterval`.
 * Calls within the interval are silently dropped (not queued).
 */
export function useAsyncThrottle<T>(
  fn: () => Promise<T>,
  minInterval: Temporal.Duration,
): () => Promise<T | undefined> {
  const lastCallTimeRef = useRef(0);
  const intervalMs = minInterval.total("milliseconds");

  return useCallback(async () => {
    const now = Date.now();
    if (now - lastCallTimeRef.current <= intervalMs) {
      return undefined;
    }
    lastCallTimeRef.current = now;
    return fn();
  }, [fn, intervalMs]);
}

// eslint-disable-next-line @redotech/no-re-exports -- TODO: remove re-export, update all import sites
export { useDebounce } from "usehooks-ts";
